
import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { CheckCircle, PlusCircle, BarChart2 } from 'lucide-react';
import { toast } from "sonner";
import analytics from '@/utils/analytics';
import { useNavigate } from 'react-router-dom';

interface SymptomsTrackerProps {
  selectedSymptoms: string[];
  setSelectedSymptoms: React.Dispatch<React.SetStateAction<string[]>>;
}

const SymptomsTracker: React.FC<SymptomsTrackerProps> = ({ 
  selectedSymptoms, 
  setSelectedSymptoms 
}) => {
  const [otherSymptom, setOtherSymptom] = useState<string>('');
  const [showOtherInput, setShowOtherInput] = useState(false);
  const navigate = useNavigate();
  
  const symptoms = [
    "Fatigue", "Headache", "Bloating", "Cramps", 
    "Mood Swings", "Insomnia", "Low Energy", "Cravings"
  ];
  
  const toggleSymptom = (symptom: string) => {
    if (symptom === 'Other') {
      setShowOtherInput(!showOtherInput);
      return;
    }
    
    setSelectedSymptoms(prev => 
      prev.includes(symptom) 
        ? prev.filter(s => s !== symptom)
        : [...prev, symptom]
    );
  };
  
  const handleOtherSymptomChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOtherSymptom(e.target.value);
  };
  
  const addOtherSymptom = async () => {
    if (otherSymptom.trim()) {
      const customSymptom = otherSymptom.trim();
      setSelectedSymptoms(prev => [...prev, customSymptom]);
      setOtherSymptom('');
      
      // Track custom symptom
      analytics.trackUserAction('custom_symptom_added', { symptom: customSymptom });
      
      toast.success('Custom symptom added!');
    }
  };
  
  const navigateToReportingDashboard = () => {
    navigate('/symptom-reporting');
  };
  
  return (
    <div className="bg-white p-4 rounded-xl border border-hormony-beige/50 shadow-sm">
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-semibold text-hormony-charcoal">
          Any symptoms today? (Select all that apply)
        </h3>
        
        <Button 
          variant="ghost" 
          size="sm" 
          className="text-hormony-coral hover:text-hormony-coral/80 hover:bg-hormony-coral/10 -mr-2"
          onClick={navigateToReportingDashboard}
        >
          <BarChart2 size={16} className="mr-1" />
          <span className="text-xs">View Trends</span>
        </Button>
      </div>
      
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {symptoms.map((symptom) => (
          <button
            key={symptom}
            onClick={() => toggleSymptom(symptom)}
            className={`flex items-center gap-2 p-2 rounded-lg border transition-all ${
              selectedSymptoms.includes(symptom)
                ? 'border-hormony-coral bg-hormony-light-coral/10 text-hormony-coral'
                : 'border-hormony-beige/50 text-hormony-charcoal/70 hover:bg-hormony-beige/10'
            }`}
          >
            {selectedSymptoms.includes(symptom) && <CheckCircle size={14} />}
            <span className="text-sm">{symptom}</span>
          </button>
        ))}
        
        {/* Other option */}
        <button
          onClick={() => toggleSymptom('Other')}
          className={`flex items-center gap-2 p-2 rounded-lg border transition-all ${
            showOtherInput
              ? 'border-hormony-coral bg-hormony-light-coral/10 text-hormony-coral'
              : 'border-hormony-beige/50 text-hormony-charcoal/70 hover:bg-hormony-beige/10'
          }`}
        >
          <PlusCircle size={14} />
          <span className="text-sm">Other</span>
        </button>
      </div>
      
      {/* Custom symptom input */}
      {showOtherInput && (
        <div className="mt-3 flex gap-2 items-center">
          <Input
            value={otherSymptom}
            onChange={handleOtherSymptomChange}
            placeholder="Type your symptom..."
            className="flex-1 border-hormony-beige focus-visible:ring-hormony-coral"
          />
          <Button 
            onClick={addOtherSymptom} 
            disabled={!otherSymptom.trim()}
            size="sm"
          >
            Add
          </Button>
        </div>
      )}
      
      {/* Display custom symptoms */}
      {selectedSymptoms.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {selectedSymptoms
            .filter(s => !symptoms.includes(s))
            .map((symptom, index) => (
              <div 
                key={index} 
                className="inline-flex items-center gap-1 bg-hormony-light-coral/10 text-hormony-coral px-2 py-1 rounded-md text-sm"
              >
                <span>{symptom}</span>
                <button 
                  onClick={() => setSelectedSymptoms(prev => prev.filter(s => s !== symptom))}
                  className="hover:text-red-500 transition-colors ml-1"
                >
                  ×
                </button>
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

export default SymptomsTracker;
